/**
 * Economy logic for the Legendary Arena game engine.
 *
 * Pure helpers for parsing card stat values, building the card stats
 * lookup at setup time, and managing per-turn attack/recruit economy.
 *
 * No boardgame.io imports. No .reduce(). No throws. Setup-time only
 * for buildCardStats; runtime-safe for economy helpers.
 */
import { matchesShieldOrHydra } from './shieldMembership.js';
// why: D-13702 / D-18706 fan-out — economy.logic.ts must resolve hero AND
// villain card-instance ext_ids identically to the deck builders so
// G.cardStats keys equal the zone-instance grammar (a superset of the hero
// deck reservoir and the exact set of villain zone instances). The shared
// emitters enforce parity by construction; divergence between sites is the
// silent lookup-miss class WP-191 fixes. Per the D-13702 RS-4 lock the
// emitters are imported (not re-implemented) from their canonical homes.
import { heroCardInstanceExtIds } from '../setup/buildHeroDeck.js';
import { villainCardInstanceExtIds } from '../villainDeck/villainDeck.setup.js';
// ---------------------------------------------------------------------------
// parseCardStatValue — deterministic parser
// ---------------------------------------------------------------------------
// why: ARCHITECTURE.md "Card Field Data Quality" — hero card fields
// contain modifier strings like "2+" and "2*"; strip modifier, parse
// integer base only. Conditional bonus semantics are WP-022.
/**
 * Parses a card stat value into a non-negative integer.
 *
 * Handles the real-world variety in card data: integers, strings with
 * trailing modifiers ("2+", "2*"), null, and undefined. Returns 0 for
 * any input that cannot be parsed to a valid non-negative integer.
 *
 * @param value - Raw card stat value from the registry.
 * @returns Non-negative integer base value.
 */
export function parseCardStatValue(value) {
    if (value === null || value === undefined) {
        return 0;
    }
    if (typeof value === 'number') {
        const floored = Math.floor(value);
        // why: negative card data values are treated as 0 — all economy
        // values must be integers >= 0
        if (floored < 0) {
            return 0;
        }
        return floored;
    }
    // String: strip trailing '+' or '*' modifier, parse integer base
    const trimmed = value.replace(/[+*]$/, '');
    const parsed = parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return 0;
    }
    return parsed;
}
// ---------------------------------------------------------------------------
// buildCardStats — setup-time card stat resolution
// ---------------------------------------------------------------------------
// why: mirrors G.villainDeckCardTypes pattern — resolve registry data
// at setup so moves never query registry at runtime
/**
 * Builds a card stats lookup from registry data at setup time.
 *
 * Iterates hero, villain, and henchman cards reachable from the match
 * config and parses their stat values into CardStatEntry records.
 *
 * @param registry - Setup-time registry reader. Accepts unknown to support
 *   narrow test mocks (CardRegistryReader). If the registry does not satisfy
 *   CardStatsRegistryReader structurally (missing listSets/getSet/full
 *   listCards), returns an empty record gracefully.
 * @param matchConfig - Match setup configuration with selected entity IDs.
 * @returns Record keyed by CardExtId with parsed stat values.
 */
export function buildCardStats(registry, matchConfig) {
    // why: narrow test mocks (CardRegistryReader) only have listCards() returning
    // { key: string }[]. We check for the full CardStatsRegistryReader interface
    // at runtime. If the registry doesn't have the required methods, we return
    // an empty record — moves handle missing cardStats entries gracefully (0/0).
    if (!isCardStatsRegistryReader(registry)) {
        return {};
    }
    const stats = {};
    // --- 1. Hero cards (from listCards — FlatCard has attack/recruit/cost) ---
    // why: WP-113 PS-7 mid-execution amendment — buildCardStats consumes
    //   set-qualified <setAbbr>/<slug> entity IDs; filter setAbbr first to
    //   avoid cross-set slug collisions (51/307 hero-slug instances collide
    //   per the PS-8 probe). Graceful-skip on parse failure: the validator
    //   is the authoritative format-error reporter, this builder is
    //   defense-in-depth (per D-10014).
    const allFlatCards = registry.listCards();
    const heroFlatCards = filterCardsByType(allFlatCards, 'hero');
    for (const heroDeckId of matchConfig.heroDeckIds) {
        const parsed = parseQualifiedIdForSetup(heroDeckId);
        if (parsed === null)
            continue;
        const deckCards = filterHeroCardsByDeckSlug(heroFlatCards, parsed.setAbbr, parsed.slug);
        for (const card of deckCards) {
            const extId = card.key;
            stats[extId] = {
                attack: parseCardStatValue(card.attack),
                recruit: parseCardStatValue(card.recruit),
                cost: parseCardStatValue(card.cost),
                // why: heroes are never fought; fightCost is for villains only
                fightCost: 0,
                fightCostMode: 'static',
                fightCostBase: 0,
                // why: WP-675 / D-24490 — icon presence is the RAW value being non-null,
                // not `attack > 0` (a "0+" card shows the icon but parses to 0).
                hasAttackIcon: card.attack != null,
                hasRecruitIcon: card.recruit != null,
                // why: WP-677 / D-24493 — S.H.I.E.L.D. Level membership: team icon shield/hydra
                // OR "S.H.I.E.L.D."/"HYDRA" in the card name.
                isShieldOrHydra: matchesShieldOrHydra(card.team, [card.name]),
            };
        }
    }
    // --- 1b. Hero card instances (slash-format ext_id with #<copyIndex>) ---
    // why: D-18706 — §1b sources its instance ids from the shared
    // heroCardInstanceExtIds emitter rather than re-walking physicalCards
    // locally, so §1b and buildHeroAbilityHooks provably share one emitter and
    // can never key heroes differently. Output is byte-identical to the prior
    // inline walk: the emitter yields canonical-face slugs (sides[0], or the
    // card slug on the rarity-fallback path), and the per-copy stat fields are
    // still resolved from heroEntry.cards by matching that slug.
    for (const heroDeckId of matchConfig.heroDeckIds) {
        const parsed = parseQualifiedIdForSetup(heroDeckId);
        if (parsed === null)
            continue;
        const setData = registry.getSet(parsed.setAbbr);
        if (!setData || typeof setData !== 'object')
            continue;
        const heroEntry = findHeroEntry(setData, parsed.slug);
        if (heroEntry === null)
            continue;
        const instances = heroCardInstanceExtIds(parsed.setAbbr, parsed.slug, heroEntry);
        for (const instance of instances) {
            const cardEntry = findEconomyCardEntryBySlug(heroEntry.cards, instance.cardSlug);
            const attack = cardEntry !== null ? parseCardStatValue(cardEntry.attack) : 0;
            const recruit = cardEntry !== null ? parseCardStatValue(cardEntry.recruit) : 0;
            const cost = cardEntry !== null ? parseCardStatValue(cardEntry.cost) : 0;
            // why: WP-675 / D-24490 — icon presence is the RAW value being non-null,
            // not `attack > 0` (a "0+" card shows the icon but parses to 0). This §1b
            // instance path is the one the runtime zone lookup reads (copy-suffixed ext_ids).
            const hasAttackIcon = cardEntry !== null && cardEntry.attack != null;
            const hasRecruitIcon = cardEntry !== null && cardEntry.recruit != null;
            // why: per-copy fresh object literal — no aliasing across keys
            // (WP-028 D-2802 aliasing prevention extended to setup-time
            // sibling-snapshot fan-out).
            stats[instance.extId] = {
                attack,
                recruit,
                cost,
                // why: heroes are never fought; fightCost is for villains only.
                fightCost: 0,
                fightCostMode: 'static',
                fightCostBase: 0,
                hasAttackIcon,
                hasRecruitIcon,
                // why: WP-677 / D-24493 — hero team icon (heroEntry.team) OR "S.H.I.E.L.D."/"HYDRA"
                // in the card name. This §1b instance path is the runtime-read key.
                isShieldOrHydra: matchesShieldOrHydra(heroEntry.team, [cardEntry?.name]),
            };
        }
    }
    // --- 2. Villain cards (from getSet — vAttack is not on FlatCard) ---
    // why: WP-113 PS-7 mid-execution amendment — set-qualified ID + named-set
    //   filter (per D-10014).
    // why: D-18704 — fan out one stat row per copy instance via the shared
    // villainCardInstanceExtIds emitter, keyed by the exact zone-instance
    // ext_id. Before WP-191 this section keyed villains by the single
    // definition FlatCard key while zones were copy-indexed, so the runtime
    // `G.cardStats[cardId]` lookup (cardId = a copy-indexed city id) always
    // missed and fightCost defaulted to 0. The FlatCard existence gate is
    // dropped: the deck builder emits an instance for every group card, so
    // keying off the same emitter guarantees cardStats covers every villain
    // zone instance (no FlatCard prerequisite).
    for (const villainGroupId of matchConfig.villainGroupIds) {
        const parsed = parseQualifiedIdForSetup(villainGroupId);
        if (parsed === null)
            continue;
        const villainCards = findVillainGroupCards(registry, parsed.setAbbr, parsed.slug);
        // why: WP-677 / D-24493 — the group name for S.H.I.E.L.D. Level membership (e.g. a
        // "HYDRA Kidnappers" group makes each card count, even if the card name omits "HYDRA").
        const villainGroupName = findVillainGroupName(registry, parsed.setAbbr, parsed.slug);
        for (const villainCard of villainCards) {
            if (typeof villainCard.slug !== 'string')
                continue;
            // Detect dynamic vAttack patterns BEFORE calling parseCardStatValue.
            // Guard: vAttack may be undefined/null for villains without an attack
            // stat — check for falsy before pattern matching to avoid TypeError.
            const rawVAttack = villainCard.vAttack;
            let fightCostMode = 'static';
            let fightCostBase = 0;
            let fightCost = 0;
            if (!rawVAttack) {
                // why: villains without vAttack (e.g. some henchmen) default to static 0
                fightCost = 0;
            }
            else if (rawVAttack === '*') {
                // why: "*" = fight cost is entirely determined by captured hero recruit costs
                fightCostMode = 'dynamic';
                fightCostBase = 0;
                fightCost = 0;
            }
            else if (typeof rawVAttack === 'string' && rawVAttack.endsWith('+')) {
                // why: "N+" = base N plus captured hero costs; parseCardStatValue strips the "+"
                fightCostMode = 'dynamic';
                fightCostBase = parseCardStatValue(rawVAttack);
                fightCost = fightCostBase;
            }
            else {
                // why: static villain — fightCostMode defaults to 'static'
                fightCost = parseCardStatValue(rawVAttack);
            }
            const instanceExtIds = villainCardInstanceExtIds(parsed.setAbbr, parsed.slug, villainCard.slug, villainCard);
            for (const extId of instanceExtIds) {
                // why: per-copy fresh object literal — no aliasing across keys.
                stats[extId] = {
                    // why: villains do not generate resources or have recruit costs
                    attack: 0,
                    recruit: 0,
                    cost: 0,
                    fightCost,
                    fightCostMode,
                    fightCostBase,
                    // why: WP-675 / D-24490 — villains carry no hero attack/recruit power icon.
                    hasAttackIcon: false,
                    hasRecruitIcon: false,
                    // why: WP-677 / D-24493 — no team icon; membership is the "S.H.I.E.L.D."/"HYDRA"
                    // substring in the villain card name OR its group name.
                    isShieldOrHydra: matchesShieldOrHydra(undefined, [villainCard.name, villainGroupName]),
                };
            }
        }
    }
    // --- 3. Henchman cards (from getSet — group-level vAttack) ---
    // why: henchmen are virtual copies (WP-014B). Their ext_id format is
    // henchman-{groupSlug}-{index}. vAttack comes from the group definition,
    // not individual cards, because henchmen are identical within a group.
    // why: WP-113 PS-7 mid-execution amendment — set-qualified ID + named-set
    //   filter (per D-10014).
    for (const henchmanGroupId of matchConfig.henchmanGroupIds) {
        const parsed = parseQualifiedIdForSetup(henchmanGroupId);
        if (parsed === null)
            continue;
        const henchmanResult = findHenchmanGroupVAttack(registry, parsed.setAbbr, parsed.slug);
        if (henchmanResult !== null) {
            const parsedFightCost = parseCardStatValue(henchmanResult.vAttack);
            // why: henchman virtual copies use index 00-09 (10 per group, per
            // WP-014B HENCHMAN_COPIES_PER_GROUP). Build stats for all copies.
            for (let copyIndex = 0; copyIndex < 10; copyIndex++) {
                const paddedIndex = String(copyIndex).padStart(2, '0');
                const extId = `henchman-${henchmanResult.groupSlug}-${paddedIndex}`;
                stats[extId] = {
                    // why: henchmen do not generate resources or have recruit costs
                    attack: 0,
                    recruit: 0,
                    cost: 0,
                    fightCost: parsedFightCost,
                    fightCostMode: 'static',
                    fightCostBase: 0,
                    // why: WP-675 / D-24490 — henchmen carry no hero attack/recruit power icon.
                    hasAttackIcon: false,
                    hasRecruitIcon: false,
                    // why: WP-677 / D-24493 — no team icon; membership is the "S.H.I.E.L.D."/"HYDRA"
                    // substring in the henchman group name (e.g. "HYDRA Kidnappers").
                    isShieldOrHydra: matchesShieldOrHydra(undefined, [henchmanResult.groupName]),
                };
            }
        }
    }
    return stats;
}
/**
 * Parses a set-qualified ID `<setAbbr>/<slug>` into its components.
 *
 * Returns null on any malformed input — empty string, missing slash,
 * multiple slashes, empty parts, or leading/trailing whitespace.
 *
 * Locally duplicated per WP-113 §6 (mid-execution amendment) — keeps
 * `economy.logic.ts` free of validator coupling. Identical rejection
 * rules to the validator's `parseQualifiedId`. Test seam.
 */
// why: WP-113 PS-7 mid-execution amendment — duplicated locally to avoid
//   validator coupling at the economy layer (per D-10014).
function parseQualifiedIdForSetup(input) {
    if (typeof input !== 'string' || input.length === 0)
        return null;
    if (input !== input.trim())
        return null;
    const slashIndex = input.indexOf('/');
    if (slashIndex === -1)
        return null;
    if (input.indexOf('/', slashIndex + 1) !== -1)
        return null;
    const setAbbr = input.slice(0, slashIndex);
    const slug = input.slice(slashIndex + 1);
    if (setAbbr.length === 0 || slug.length === 0)
        return null;
    return { setAbbr, slug };
}
// why: WP-580 / D-24389 + WP-731 / D-24552 + WP-736 / D-24556 — every
// lazily-materialized turn-scoped field (`recruitSpendableAsAttack`, `drawsLocked`,
// and now the Excessive Violence ledger `excessiveViolencePlayedCards` +
// `excessiveViolenceUsedThisTurn`) must SURVIVE every TurnEconomy rebuild.
// addResources/spendAttack/spendRecruit reconstruct the object from an explicit literal
// (not a spread), so without this a later same-turn spend would silently drop a flag set
// earlier by God of Thunder or Venompool — or, per D-24556, the EV ledger of cards
// enrolled at play would be dropped by the very fight (`spendFightCost` → the rebuild
// helpers) that is supposed to fire them, so no enrolled EV ability could ever fire.
// This is the SINGLE carry chokepoint — the setters (`enableRecruitSpendableAsAttack`,
// `enableDrawLock`, `enrollExcessiveViolenceCard`, `markExcessiveViolenceUsed`) spread it
// too, so setting one field can never drop another.
// Per-field conditional add (not `drawsLocked: economy.drawsLocked`) keeps each
// field ABSENT when unset under exactOptionalPropertyTypes, preserving the
// lazy-materialization guarantee that both hash oracles stay byte-stable.
/**
 * Carries the lazily-materialized turn-scoped fields forward across a
 * TurnEconomy rebuild.
 *
 * @param economy - Current turn economy state.
 * @returns The subset of `{ recruitSpendableAsAttack, drawsLocked,
 *   excessiveViolencePlayedCards, excessiveViolenceUsedThisTurn,
 *   isPlayBothSidesActive, bothSidesPlayedCardIds }` that is set
 *   (each key present only when its field is set), else `{}`.
 */
function carryConversionFlag(economy) {
    const carried = {};
    if (economy.recruitSpendableAsAttack !== undefined) {
        carried.recruitSpendableAsAttack = economy.recruitSpendableAsAttack;
    }
    if (economy.drawsLocked !== undefined) {
        carried.drawsLocked = economy.drawsLocked;
    }
    // why: WP-736 / D-24556 — carry the EV ledger array (not just the two conversion flags):
    // the ledger is what keeps the enrolled EV cards alive across the addResources /
    // spendAttack / spendRecruit rebuilds that a play + the firing fight run, so
    // `fireExcessiveViolencePlays` still sees the cards enrolled earlier this turn.
    if (economy.excessiveViolencePlayedCards !== undefined) {
        carried.excessiveViolencePlayedCards = economy.excessiveViolencePlayedCards;
    }
    if (economy.excessiveViolenceUsedThisTurn !== undefined) {
        carried.excessiveViolenceUsedThisTurn = economy.excessiveViolenceUsedThisTurn;
    }
    // why: WP-780 / D-24619 — carry the Penumbra flag and both-sides ledger, or a later
    // same-turn addResources rebuild would drop them and the picker would reappear.
    if (economy.isPlayBothSidesActive !== undefined) {
        carried.isPlayBothSidesActive = economy.isPlayBothSidesActive;
    }
    if (economy.bothSidesPlayedCardIds !== undefined) {
        carried.bothSidesPlayedCardIds = economy.bothSidesPlayedCardIds;
    }
    // why: WP-790 / D-24652 — carry the restricted-attack sub-ledger, or a later same-turn
    // addResources / spend rebuild would drop it and the restricted remainder would turn back
    // into plain attack. Copied as a new array of copied grants so no rebuild aliases the
    // prior economy's ledger.
    if (economy.restrictedAttack !== undefined) {
        carried.restrictedAttack = copyRestrictedAttackGrants(economy.restrictedAttack);
    }
    // why: WP-794 / D-24663 — carry the fight-cost reduction list ONLY when present, so a turn
    // with no reduction keeps the key absent and both hash oracles stay byte-stable. Without the
    // carry, the very next addResources / spend rebuild would drop the reductions and the next
    // fight would cost full price. Copied as a new array of copied entries so no rebuild aliases
    // the prior economy's list.
    if (economy.fightCostReductions !== undefined) {
        carried.fightCostReductions = copyFightCostReductions(economy.fightCostReductions);
    }
    // why: WP-803 / D-24692 — carry the Out of Time played ledger ONLY when present, so a turn
    // with no Out of Time card keeps the key absent and both hash oracles stay byte-stable.
    // Without the carry, the next addResources / spend rebuild would drop it and a later
    // Superpower would stop seeing the set-aside card as played this turn.
    if (economy.outOfTimePlayedCardIds !== undefined) {
        carried.outOfTimePlayedCardIds = economy.outOfTimePlayedCardIds;
    }
    // why: WP-842 / D-24776 — carry the once-per-turn Sidekick recruit flag and the
    // returned-Sidekick count ONLY when present (omit-when-absent keeps both hash oracles
    // byte-stable). Without the carry, the next addResources / spend rebuild would drop the
    // flag (a second Sidekick recruit would be allowed) or the count (a returned Sidekick
    // would stop counting as played this turn).
    if (economy.hasRecruitedSidekickThisTurn !== undefined) {
        carried.hasRecruitedSidekickThisTurn = economy.hasRecruitedSidekickThisTurn;
    }
    if (economy.sidekicksReturnedThisTurn !== undefined) {
        carried.sidekicksReturnedThisTurn = economy.sidekicksReturnedThisTurn;
    }
    // why: WP-858 / D-24794 — carry the recruited-Hero cost record and the used-Ritual played
    // ledger ONLY when present (omit-when-absent keeps both hash oracles byte-stable). Without
    // the carry, the next addResources / spend rebuild would drop the record (Staff of Legba
    // would stop being usable) or the ledger (a used Ritual would stop counting as played).
    if (economy.highestRecruitedHeroCost !== undefined) {
        carried.highestRecruitedHeroCost = economy.highestRecruitedHeroCost;
    }
    if (economy.ritualUsedPlayedCardIds !== undefined) {
        carried.ritualUsedPlayedCardIds = economy.ritualUsedPlayedCardIds;
    }
    return carried;
}
/**
 * Copies a fight-cost reduction list: a new array of new entry objects.
 *
 * @param reductions - The list to copy.
 * @returns A copy that shares no array or object with `reductions`.
 */
function copyFightCostReductions(reductions) {
    const copied = [];
    for (const reduction of reductions) {
        copied.push({
            target: reduction.target,
            amount: reduction.amount,
            sourceCardId: reduction.sourceCardId,
        });
    }
    return copied;
}
/**
 * Copies a restricted-attack ledger: a new array of new grant objects, each with
 * its own copied `targets` array.
 *
 * @param grants - The ledger to copy.
 * @returns A deep copy that shares no array or object with `grants`.
 */
function copyRestrictedAttackGrants(grants) {
    const copied = [];
    for (const grant of grants) {
        copied.push({
            remaining: grant.remaining,
            targets: [...grant.targets],
            sourceCardId: grant.sourceCardId,
        });
    }
    return copied;
}
/**
 * Returns available (unspent) attack points.
 *
 * @param economy - Current turn economy state.
 * @returns Available attack points (attack minus spentAttack).
 */
export function getAvailableAttack(economy) {
    return economy.attack - economy.spentAttack;
}
/**
 * Returns the attack a fight can be funded with — available attack, plus
 * unspent recruit when the WP-580 recruit-as-attack conversion is active
 * this turn (God of Thunder). Equal to `getAvailableAttack` when the flag
 * is unset, so every non-conversion turn is byte-identical to pre-WP-580.
 *
 * @param economy - Current turn economy state.
 * @returns Attack points spendable toward fight costs this turn.
 */
export function getSpendableAttack(economy) {
    // why: WP-790 / D-24652 — restricted ("usable only against …") attack stays inside
    // `attack` but may pay only for an eligible target, so the target-free spendable figure
    // excludes its unspent remainder. The max(0, …) clamp is defensive: the spend helpers
    // keep `attack - spentAttack >= remaining`, but a negative spendable figure must never
    // reach a gate or the projected `availableAttack`.
    const unrestrictedAttack = economy.attack - economy.spentAttack - getRestrictedAttackRemaining(economy);
    const availableAttack = Math.max(0, unrestrictedAttack);
    if (economy.recruitSpendableAsAttack === true) {
        return availableAttack + (economy.recruit - economy.spentRecruit);
    }
    return availableAttack;
}
/**
 * Returns the total unspent restricted attack this turn (WP-790 / D-24652): the
 * sum of every grant's `remaining`, or 0 when no restricted grant was played.
 *
 * @param economy - Current turn economy state.
 * @returns The restricted remainder still inside `attack - spentAttack`.
 */
export function getRestrictedAttackRemaining(economy) {
    if (economy.restrictedAttack === undefined) {
        return 0;
    }
    let total = 0;
    for (const grant of economy.restrictedAttack) {
        total += grant.remaining;
    }
    return total;
}
/**
 * Sums the unspent restricted attack that can pay for a fight against `target`
 * (WP-790 / D-24652). This is the ONE eligibility rule: the engine gates and the
 * arena-client Fight buttons both call it.
 *
 * @param grants - Restricted grants (the engine ledger or the UI projection).
 * @param target - The fight target, or undefined (no restricted attack applies).
 * @returns The sum of `remaining` over the grants whose `targets` include `target`.
 */
export function sumRestrictedAttackForTarget(grants, target) {
    if (target === undefined) {
        return 0;
    }
    let total = 0;
    for (const grant of grants) {
        if (grant.targets.includes(target)) {
            total += grant.remaining;
        }
    }
    return total;
}
/**
 * Returns the attack that can fund a fight against `target` (WP-790 / D-24652):
 * the target-free spendable figure plus the restricted attack eligible for it.
 *
 * @param economy - Current turn economy state.
 * @param target - The fight target (a City space name or 'mastermind').
 * @returns Attack points spendable toward this target's fight cost.
 */
export function getSpendableAttackForTarget(economy, target) {
    return getSpendableAttack(economy) + sumRestrictedAttackForTarget(economy.restrictedAttack ?? [], target);
}
/** Display names for the fight targets (WP-790 / D-24652). */
const ATTACK_TARGET_LABELS = {
    sewers: 'Sewers',
    bank: 'Bank',
    rooftops: 'Rooftops',
    streets: 'Streets',
    bridge: 'Bridge',
    mastermind: 'Mastermind',
    'unnamed-city-space': 'an unnamed City space',
};
/**
 * Formats restricted-attack targets for the log line and the economy-bar chip,
 * e.g. `['sewers', 'bridge', 'mastermind']` → `"Sewers or Bridge or Mastermind"`.
 *
 * @param targets - The targets, in canonical order.
 * @returns The display names joined with `" or "`.
 */
export function formatAttackTargets(targets) {
    const labels = [];
    for (const target of targets) {
        labels.push(ATTACK_TARGET_LABELS[target]);
    }
    return labels.join(' or ');
}
/**
 * Returns available (unspent) recruit points.
 *
 * @param economy - Current turn economy state.
 * @returns Available recruit points (recruit minus spentRecruit).
 */
export function getAvailableRecruit(economy) {
    return economy.recruit - economy.spentRecruit;
}
/**
 * Adds attack and recruit resources to the economy.
 *
 * @param economy - Current turn economy state.
 * @param attack - Attack points to add.
 * @param recruit - Recruit points to add.
 * @returns New TurnEconomy with updated totals.
 */
export function addResources(economy, attack, recruit) {
    return {
        attack: economy.attack + attack,
        recruit: economy.recruit + recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count across every
        // economy rebuild (mirrors woundsDrawn), so a later same-turn play/spend does
        // not silently reset the "drew N cards this turn" gate.
        cardsDrawn: economy.cardsDrawn,
        ...carryConversionFlag(economy),
    };
}
/**
 * Records an attack point spend.
 *
 * @param economy - Current turn economy state.
 * @param amount - Attack points to spend.
 * @returns New TurnEconomy with incremented spentAttack.
 */
export function spendAttack(economy, amount) {
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack + amount,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count across every
        // economy rebuild (mirrors woundsDrawn), so a later same-turn play/spend does
        // not silently reset the "drew N cards this turn" gate.
        cardsDrawn: economy.cardsDrawn,
        ...carryConversionFlag(economy),
    };
}
/**
 * Records a recruit point spend.
 *
 * @param economy - Current turn economy state.
 * @param amount - Recruit points to spend.
 * @returns New TurnEconomy with incremented spentRecruit.
 */
export function spendRecruit(economy, amount) {
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit + amount,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count across every
        // economy rebuild (mirrors woundsDrawn), so a later same-turn play/spend does
        // not silently reset the "drew N cards this turn" gate.
        cardsDrawn: economy.cardsDrawn,
        ...carryConversionFlag(economy),
    };
}
/**
 * Enables the WP-580 recruit-as-attack conversion for the current turn.
 *
 * Called by the `recruit-as-attack` hero effect (God of Thunder). Sets the
 * flag; every other field is carried unchanged. `resetTurnEconomy` clears it
 * at the next turn start.
 *
 * @param economy - Current turn economy state.
 * @returns New TurnEconomy with `recruitSpendableAsAttack` set true.
 */
export function enableRecruitSpendableAsAttack(economy) {
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count across every
        // economy rebuild (mirrors woundsDrawn), so a later same-turn play/spend does
        // not silently reset the "drew N cards this turn" gate.
        cardsDrawn: economy.cardsDrawn,
        // why: WP-731 / D-24552 — spread the single carry chokepoint so setting the
        // recruit-as-attack flag never drops a `drawsLocked` set earlier the same
        // turn (Venompool + God of Thunder is a legal loadout). The explicit
        // assignment below then sets THIS setter's own flag.
        ...carryConversionFlag(economy),
        recruitSpendableAsAttack: true,
    };
}
/**
 * Enables the WP-731 turn-scoped draw lock for the current turn.
 *
 * Called by the `no-more-draws` hero effect (Venompool's Shenanigans). Sets
 * `drawsLocked`; every other field — including a `recruitSpendableAsAttack` set
 * earlier this turn — is carried unchanged through the single carry chokepoint.
 * `resetTurnEconomy` clears it at the next turn start.
 *
 * @param economy - Current turn economy state.
 * @returns New TurnEconomy with `drawsLocked` set true.
 */
export function enableDrawLock(economy) {
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count (mirrors
        // woundsDrawn) so a later same-turn play/spend does not reset the gate.
        cardsDrawn: economy.cardsDrawn,
        // why: WP-731 / D-24552 — spread the single carry chokepoint so setting the
        // draw lock never drops a `recruitSpendableAsAttack` set earlier this turn.
        ...carryConversionFlag(economy),
        drawsLocked: true,
    };
}
/**
 * Enrolls an "Excessive Violence" card into the current turn's EV ledger
 * (WP-736 / D-24556).
 *
 * Called by the `excessive-violence` enroll handler (heroEffectExcessiveViolence) when an
 * allowlisted EV card is PLAYED. Appends `cardId` to `excessiveViolencePlayedCards`,
 * materializing the array on the first call. Append order is fire order; duplicates are
 * intentionally kept (two copies of the same-named card each fire, per glossary id 30).
 * Every other field — including a `drawsLocked` / `recruitSpendableAsAttack` set earlier
 * this turn — is carried unchanged through the single carry chokepoint.
 * `resetTurnEconomy` drops the ledger at the next turn start.
 *
 * @param economy - Current turn economy state.
 * @param cardId - The played EV card's CardExtId to enrol.
 * @returns New TurnEconomy with `cardId` appended to the EV ledger.
 */
export function enrollExcessiveViolenceCard(economy, cardId) {
    // why: WP-736 / D-24556 — materialize the ledger on first enrolment (absent until then,
    // preserving the omit-when-off / hash-stable guarantee), then append. Build the next
    // array from the carried copy so the rebuild below never aliases the prior turn state.
    const nextPlayedCards = [...(economy.excessiveViolencePlayedCards ?? []), cardId];
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count (mirrors woundsDrawn).
        cardsDrawn: economy.cardsDrawn,
        // why: WP-736 / D-24556 — spread the single carry chokepoint so enrolling an EV card
        // never drops a `recruitSpendableAsAttack` / `drawsLocked` / `excessiveViolenceUsedThisTurn`
        // set earlier this turn. The explicit assignment below then sets THIS setter's own field.
        ...carryConversionFlag(economy),
        excessiveViolencePlayedCards: nextPlayedCards,
    };
}
/**
 * Marks that the current player has fought "using Excessive Violence" this turn
 * (WP-736 / D-24556).
 *
 * Called by the fight moves (fightVillain / fightMastermind) the first time a fight
 * resolves with the +1-attack overspend. Sets `excessiveViolenceUsedThisTurn`; the fight
 * moves then bar a second EV fight the same turn. Every other field — including the EV
 * ledger itself — is carried unchanged through the single carry chokepoint.
 * `resetTurnEconomy` clears it at the next turn start.
 *
 * @param economy - Current turn economy state.
 * @returns New TurnEconomy with `excessiveViolenceUsedThisTurn` set true.
 */
export function markExcessiveViolenceUsed(economy) {
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count (mirrors woundsDrawn).
        cardsDrawn: economy.cardsDrawn,
        // why: WP-736 / D-24556 — spread the single carry chokepoint so setting the once-per-turn
        // guard never drops the EV ledger (or the two conversion flags) set earlier this turn.
        ...carryConversionFlag(economy),
        excessiveViolenceUsedThisTurn: true,
    };
}
/**
 * Activates cvwr Cloak & Dagger Penumbra's "play both sides" for the current turn
 * (WP-780 / D-24619).
 *
 * Called by the `play-both-sides` hero effect when Penumbra is played. Sets
 * `isPlayBothSidesActive`; idempotent (a second Penumbra rebuilds the same shape). Every
 * other field is carried unchanged through the single carry chokepoint.
 * `resetTurnEconomy` drops the flag at the next turn start.
 *
 * @param economy - Current turn economy state.
 * @returns New TurnEconomy with `isPlayBothSidesActive` set true.
 */
export function enablePlayBothSides(economy) {
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count (mirrors woundsDrawn).
        cardsDrawn: economy.cardsDrawn,
        // why: WP-780 / D-24619 — spread the single carry chokepoint so activating Penumbra never
        // drops a flag or ledger set earlier this turn. The explicit assignment then sets this one.
        ...carryConversionFlag(economy),
        isPlayBothSidesActive: true,
    };
}
/**
 * Marks this turn's one Sidekick recruit as used (WP-842 / D-24776).
 *
 * Called only by the `recruitSidekick` move. Sets `hasRecruitedSidekickThisTurn`; every
 * other field is carried unchanged through the single carry chokepoint.
 * `resetTurnEconomy` drops the flag at the next turn start.
 *
 * @param economy - Current turn economy state.
 * @returns New TurnEconomy with `hasRecruitedSidekickThisTurn` set true.
 */
export function markSidekickRecruited(economy) {
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        cardsDrawn: economy.cardsDrawn,
        // why: spread the single carry chokepoint so marking the recruit never drops a flag or
        // ledger set earlier this turn. The explicit assignment then sets this one.
        ...carryConversionFlag(economy),
        hasRecruitedSidekickThisTurn: true,
    };
}
/**
 * Records one played Sidekick returned to the Sidekick Stack this turn (WP-842 / D-24776).
 *
 * Called by `returnPlayedSidekick`. Increments `sidekicksReturnedThisTurn`, materializing it
 * at 1 on the first call. Every other field is carried unchanged through the single carry
 * chokepoint. `resetTurnEconomy` drops the count at the next turn start.
 *
 * @param economy - Current turn economy state.
 * @returns New TurnEconomy with `sidekicksReturnedThisTurn` one higher.
 */
export function recordSidekickReturned(economy) {
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        cardsDrawn: economy.cardsDrawn,
        // why: spread the single carry chokepoint so recording the return never drops a flag or
        // ledger set earlier this turn. The explicit assignment then sets this one.
        ...carryConversionFlag(economy),
        sidekicksReturnedThisTurn: (economy.sidekicksReturnedThisTurn ?? 0) + 1,
    };
}
/**
 * Records a split card's inPlay entry (its face-a id) as played both-sides this turn
 * (WP-780 / D-24619).
 *
 * Called by `playBothSplitFaces` BETWEEN the two faces. Appends `cardId` to
 * `bothSidesPlayedCardIds`, materializing the array on the first call. Every other field is
 * carried unchanged. `resetTurnEconomy` drops the ledger at the next turn start.
 *
 * @param economy - Current turn economy state.
 * @param cardId - The inPlay entry id (face a) of the split card played both-sides.
 * @returns New TurnEconomy with `cardId` appended to `bothSidesPlayedCardIds`.
 */
export function markBothSidesPlayed(economy, cardId) {
    // why: WP-780 / D-24619 — build a NEW array (mirrors enrollExcessiveViolenceCard) so the
    // rebuild never aliases the prior economy's ledger.
    const nextBothSidesPlayed = [...(economy.bothSidesPlayedCardIds ?? []), cardId];
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count (mirrors woundsDrawn).
        cardsDrawn: economy.cardsDrawn,
        // why: WP-780 / D-24619 — spread the single carry chokepoint so recording a both-sides
        // play never drops the Penumbra flag (or any other lazy field) set earlier this turn.
        ...carryConversionFlag(economy),
        bothSidesPlayedCardIds: nextBothSidesPlayed,
    };
}
/**
 * Records the printed cost of a Hero the current player just recruited (WP-858 / D-24794).
 *
 * Keeps the HIGHEST cost recruited this turn in `highestRecruitedHeroCost`, materializing it
 * on the first call. Every other field is carried unchanged. `resetTurnEconomy` drops it at
 * the next turn start.
 *
 * @param economy - Current turn economy state.
 * @param cost - The recruited Hero's printed cost (the recruit move's own `requiredCost`).
 * @returns New TurnEconomy with the highest recorded cost.
 */
export function recordRecruitedHeroCost(economy, cost) {
    // why: WP-858 / D-24794 RS-1 — "that Hero's cost" when several Heroes were recruited is the
    // highest; the player would always choose it (D-24470 pure-upside posture).
    let highestCost = cost;
    if (economy.highestRecruitedHeroCost !== undefined && economy.highestRecruitedHeroCost > cost) {
        highestCost = economy.highestRecruitedHeroCost;
    }
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        cardsDrawn: economy.cardsDrawn,
        // why: WP-858 / D-24794 — spread the single carry chokepoint so recording a recruit never
        // drops another lazy field set earlier this turn.
        ...carryConversionFlag(economy),
        highestRecruitedHeroCost: highestCost,
    };
}
/**
 * Records a Ritual Artifact the current player played this turn and then used, so it still
 * counts as played this turn (WP-858 / D-24794).
 *
 * Appends `cardId` to `ritualUsedPlayedCardIds`, materializing the array on the first call.
 * Every other field is carried unchanged. `resetTurnEconomy` drops the ledger at the next
 * turn start.
 *
 * @param economy - Current turn economy state.
 * @param cardId - The Ritual Artifact used from `inPlay`.
 * @returns New TurnEconomy with `cardId` appended to `ritualUsedPlayedCardIds`.
 */
export function recordRitualUsedPlayed(economy, cardId) {
    // why: WP-858 / D-24794 — build a NEW array (mirrors recordOutOfTimePlayed) so the rebuild
    // never aliases the prior economy's ledger.
    const nextRitualUsedPlayed = [...(economy.ritualUsedPlayedCardIds ?? []), cardId];
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        cardsDrawn: economy.cardsDrawn,
        // why: WP-858 / D-24794 — spread the single carry chokepoint so recording a used Ritual
        // never drops another lazy field set earlier this turn.
        ...carryConversionFlag(economy),
        ritualUsedPlayedCardIds: nextRitualUsedPlayed,
    };
}
/**
 * Records a card that went Out of Time, or returned from Out of Time, as played this turn
 * (WP-803 / D-24692).
 *
 * Called by the `out-of-time` hero handler when it sets the card aside, and by
 * `replayOutOfTimeCards` before the turn-2 play. Appends `cardId` to
 * `outOfTimePlayedCardIds`, materializing the array on the first call. Every other field is
 * carried unchanged. `resetTurnEconomy` drops the ledger at the next turn start.
 *
 * @param economy - Current turn economy state.
 * @param cardId - The card that went Out of Time or returned from it.
 * @returns New TurnEconomy with `cardId` appended to `outOfTimePlayedCardIds`.
 */
export function recordOutOfTimePlayed(economy, cardId) {
    // why: WP-803 / D-24692 — build a NEW array (mirrors markBothSidesPlayed) so the rebuild
    // never aliases the prior economy's ledger.
    const nextOutOfTimePlayed = [...(economy.outOfTimePlayedCardIds ?? []), cardId];
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        cardsDrawn: economy.cardsDrawn,
        // why: WP-803 / D-24692 — spread the single carry chokepoint so recording an Out of Time
        // card never drops another lazy field set earlier this turn.
        ...carryConversionFlag(economy),
        outOfTimePlayedCardIds: nextOutOfTimePlayed,
    };
}
/**
 * Records a fight-cost spend, debiting attack first and — when the WP-580
 * conversion is active this turn — funding any remainder from unspent recruit.
 *
 * The caller gates affordability on `getSpendableAttack`, so `cost` is never
 * larger than what attack + convertible recruit can cover. When the flag is
 * unset the remainder is always 0, so the result is byte-identical to a plain
 * `spendAttack(economy, cost)`.
 *
 * @param economy - Current turn economy state.
 * @param cost - The fight cost to pay.
 * @returns New TurnEconomy with the cost debited (attack first, then recruit).
 */
export function spendFightCost(economy, cost) {
    const availableAttack = economy.attack - economy.spentAttack;
    // why: WP-580 / D-24389 — attack-first-then-recruit spend order. Attack is
    // the native fight resource; recruit converts only to cover the shortfall.
    const fromAttack = cost < availableAttack ? cost : availableAttack;
    const fromRecruit = cost - fromAttack;
    let next = spendAttack(economy, fromAttack);
    if (fromRecruit > 0) {
        next = spendRecruit(next, fromRecruit);
    }
    return next;
}
/**
 * Adds a "usable only against …" attack grant (WP-790 / D-24652).
 *
 * `attack += amount` (the grant still counts toward the turn's attack made) and a
 * new grant is appended to the lazily-materialized `restrictedAttack` ledger. Every
 * other field is carried unchanged through the single carry chokepoint.
 *
 * @param economy - Current turn economy state.
 * @param amount - The restricted attack granted.
 * @param targets - Where it may be spent, in canonical order.
 * @param sourceCardId - The hero card that granted it.
 * @returns New TurnEconomy with the attack added and the grant recorded.
 */
export function addRestrictedAttack(economy, amount, targets, sourceCardId) {
    // why: WP-790 / D-24652 — printed "usable only against …" attack (rules v23, the Liberate
    // entry). It stays inside `attack`, so the turn total, the stats and every "attack you
    // made" condition count it; the ledger entry is what keeps it off ineligible fights.
    // Materialized on the first grant only (absent until then), so a turn with no restricted
    // grant serializes byte-identically and neither hash oracle moves.
    const nextGrants = copyRestrictedAttackGrants(economy.restrictedAttack ?? []);
    nextGrants.push({ remaining: amount, targets: [...targets], sourceCardId });
    return {
        attack: economy.attack + amount,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count (mirrors woundsDrawn).
        cardsDrawn: economy.cardsDrawn,
        ...carryConversionFlag(economy),
        restrictedAttack: nextGrants,
    };
}
/**
 * Records a fight-cost spend against a known target (WP-790 / D-24652).
 *
 * Pays in three steps: (1) restricted grants eligible for `target`,
 * narrowest-first; (2) unrestricted attack; (3) unspent recruit, only when the
 * WP-580 conversion is active. The caller gates affordability on
 * `getSpendableAttackForTarget`. With no restricted grant the result equals
 * `spendFightCost(economy, cost)`.
 *
 * It never delegates to `spendFightCost`: that function's `attack - spentAttack`
 * includes the remainder of INELIGIBLE grants, so under the WP-580 conversion it
 * would spend that restricted attack instead of recruit.
 *
 * @param economy - Current turn economy state.
 * @param cost - The fight cost to pay (including any Excessive Violence extra).
 * @param target - The fight target.
 * @returns New TurnEconomy with the cost debited.
 */
export function spendFightCostForTarget(economy, cost, target) {
    let costLeft = cost;
    let next = economy;
    // Step 1: eligible restricted grants, narrowest-first.
    if (economy.restrictedAttack !== undefined) {
        const grants = copyRestrictedAttackGrants(economy.restrictedAttack);
        let paidFromGrants = 0;
        for (const grantIndex of orderEligibleGrantsNarrowestFirst(grants, target)) {
            if (costLeft <= 0) {
                break;
            }
            const grant = grants[grantIndex];
            const fromGrant = Math.min(costLeft, grant.remaining);
            grant.remaining -= fromGrant;
            costLeft -= fromGrant;
            paidFromGrants += fromGrant;
        }
        // why: zero-remaining grants stay in the array (stable indices). The paid amount moves
        // into spentAttack, so `attack - spentAttack` stays the true unspent total and the
        // invariant `attack - spentAttack >= remaining` holds.
        next = { ...spendAttack(economy, paidFromGrants), restrictedAttack: grants };
    }
    // Step 2: unrestricted attack — never the remainder of a grant this target cannot use.
    const unrestrictedAttack = next.attack - next.spentAttack - getRestrictedAttackRemaining(next);
    const fromAttack = Math.max(0, Math.min(costLeft, unrestrictedAttack));
    next = spendAttack(next, fromAttack);
    costLeft -= fromAttack;
    // Step 3: recruit, only under the WP-580 recruit-as-attack conversion.
    if (costLeft > 0 && next.recruitSpendableAsAttack === true) {
        next = spendRecruit(next, costLeft);
    }
    return next;
}
/**
 * Returns the indices of the grants eligible for `target`, narrowest-first: the
 * fewest `targets` first, ties broken by grant (array) order.
 *
 * @param grants - The restricted-attack ledger.
 * @param target - The fight target.
 * @returns Eligible grant indices in spend order.
 */
function orderEligibleGrantsNarrowestFirst(grants, target) {
    const eligibleIndices = [];
    for (let grantIndex = 0; grantIndex < grants.length; grantIndex++) {
        const grant = grants[grantIndex];
        if (grant.remaining > 0 && grant.targets.includes(target)) {
            eligibleIndices.push(grantIndex);
        }
    }
    // why: WP-790 / D-24652 — narrowest-first keeps the most flexible attack for later
    // fights. Deterministic (target count, then grant order), so no player prompt is needed.
    eligibleIndices.sort((leftIndex, rightIndex) => {
        const widthDifference = grants[leftIndex].targets.length - grants[rightIndex].targets.length;
        if (widthDifference !== 0) {
            return widthDifference;
        }
        return leftIndex - rightIndex;
    });
    return eligibleIndices;
}
/**
 * Records a fight-cost reduction for the rest of the turn (WP-794 / D-24663).
 *
 * Appends one `{ target, amount, sourceCardId }` entry to the lazily-materialized
 * `fightCostReductions` list (the old list, or `[]`). Every other field is carried
 * unchanged through the single carry chokepoint. `resetTurnEconomy` drops the list
 * at the next turn start.
 *
 * @param economy - Current turn economy state.
 * @param target - The City space, or `'mastermind'`, whose fights cost less.
 * @param amount - How much less each such fight costs.
 * @param sourceCardId - The hero card that played the reduction line.
 * @returns New TurnEconomy with the reduction appended.
 */
export function addFightCostReduction(economy, target, amount, sourceCardId) {
    const nextReductions = copyFightCostReductions(economy.fightCostReductions ?? []);
    nextReductions.push({ target, amount, sourceCardId });
    return {
        attack: economy.attack,
        recruit: economy.recruit,
        spentAttack: economy.spentAttack,
        spentRecruit: economy.spentRecruit,
        piercing: economy.piercing,
        woundsDrawn: economy.woundsDrawn,
        // why: WP-665 / D-24476 — carry the per-turn effect-draw count (mirrors woundsDrawn).
        cardsDrawn: economy.cardsDrawn,
        ...carryConversionFlag(economy),
        fightCostReductions: nextReductions,
    };
}
/**
 * Returns the total fight-cost reduction for one target this turn (WP-794 / D-24663).
 *
 * Sums `amount` over the `fightCostReductions` entries whose `target` equals
 * `target`. Entries stack and are never spent, so every fight against an eligible
 * target reads the full sum.
 *
 * @param economy - Current turn economy state.
 * @param target - The fight target (a City space name, or `'mastermind'`).
 * @returns The summed reduction, or 0 when the list is absent or has no match.
 */
export function getFightCostReduction(economy, target) {
    if (economy.fightCostReductions === undefined) {
        return 0;
    }
    let totalReduction = 0;
    for (const reduction of economy.fightCostReductions) {
        if (reduction.target === target) {
            totalReduction += reduction.amount;
        }
    }
    return totalReduction;
}
/**
 * Returns a fresh TurnEconomy with all values at zero.
 *
 * Called at the start of each player turn and during initial setup. Returns
 * the base shape with NO lazy turn-scoped flags, so both `recruitSpendableAsAttack`
 * (WP-580) and `drawsLocked` (WP-731) are dropped at every turn boundary (never
 * persisted across turns).
 *
 * @returns TurnEconomy with all fields set to 0.
 */
export function resetTurnEconomy() {
    // why: WP-665 / D-24476 — cardsDrawn starts at 0 each turn (the effect-draw
    // count the "drew N cards this turn" gate reads).
    return { attack: 0, recruit: 0, spentAttack: 0, spentRecruit: 0, piercing: 0, woundsDrawn: 0, cardsDrawn: 0 };
}
// ---------------------------------------------------------------------------
// Runtime type guard
// ---------------------------------------------------------------------------
/**
 * Runtime type guard for CardStatsRegistryReader.
 *
 * Returns true if the registry object has the required methods (listCards,
 * listSets, getSet). Narrow test mocks that only implement
 * CardRegistryReader will return false.
 */
function isCardStatsRegistryReader(registry) {
    if (!registry || typeof registry !== 'object')
        return false;
    const candidate = registry;
    return (typeof candidate.listCards === 'function' &&
        typeof candidate.listSets === 'function' &&
        typeof candidate.getSet === 'function');
}
// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
/**
 * Filters flat cards to only those with the given cardType.
 */
function filterCardsByType(cards, cardType) {
    const result = [];
    for (const card of cards) {
        if (card.cardType === cardType) {
            result.push(card);
        }
    }
    return result;
}
/**
 * Filters hero flat cards by setAbbr first, then by hero deck slug.
 *
 * Hero FlatCard key format: {setAbbr}-hero-{heroSlug}-{slot}.
 * Per WP-113 PS-7 (mid-execution amendment): filter by setAbbr first to
 * avoid cross-set hero-slug collisions (51/307 instances).
 */
// why: WP-113 PS-7 mid-execution amendment — Builder Filtering Order —
//   iterate named set only (per D-10014).
function filterHeroCardsByDeckSlug(heroCards, targetSetAbbr, heroDeckSlug) {
    const result = [];
    for (const card of heroCards) {
        if (card.setAbbr !== targetSetAbbr)
            continue;
        const heroSlug = extractHeroSlug(card);
        if (heroSlug === heroDeckSlug) {
            result.push(card);
        }
    }
    return result;
}
/**
 * Extracts the hero slug from a hero FlatCard.
 *
 * Key format: {setAbbr}-hero-{heroSlug}-{slot}
 * The heroSlug is between "hero-" and the last "-{slot}" segment.
 */
function extractHeroSlug(card) {
    const prefix = `${card.setAbbr}-hero-`;
    if (!card.key.startsWith(prefix)) {
        return '';
    }
    // why: slot is always the last segment after the final "-"
    const afterPrefix = card.key.slice(prefix.length);
    const lastDashIndex = afterPrefix.lastIndexOf('-');
    if (lastDashIndex === -1) {
        return '';
    }
    return afterPrefix.slice(0, lastDashIndex);
}
/**
 * Finds a hero entry within a set's heroes[] array by slug.
 *
 * Returns null if the set data is malformed (missing heroes array) or
 * the named hero is not present. Used by the WP-135 hero card-instance
 * walk inside buildCardStats.
 */
function findHeroEntry(setData, heroSlug) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.heroes))
        return null;
    for (const entry of candidate.heroes) {
        if (!entry || typeof entry !== 'object')
            continue;
        const heroEntry = entry;
        if (typeof heroEntry.slug !== 'string')
            continue;
        if (heroEntry.slug !== heroSlug)
            continue;
        if (!Array.isArray(heroEntry.cards))
            continue;
        return heroEntry;
    }
    return null;
}
/**
 * Finds a hero card entry by slug within the cards array.
 *
 * Used by the physicalCards migration to resolve stat fields for the
 * canonical face slug (sides[0] per D-14101).
 */
function findEconomyCardEntryBySlug(cards, slug) {
    for (const cardEntry of cards) {
        if (!cardEntry || typeof cardEntry !== 'object')
            continue;
        if (cardEntry.slug === slug)
            return cardEntry;
    }
    return null;
}
/**
 * Finds villain cards for a group within the named set's villains[].
 *
 * No cross-set fallback exists — returns [] if the named set is not
 * loaded or the group slug is not present in it.
 */
// why: WP-113 PS-7 mid-execution amendment — Builder Filtering Order —
//   iterate named set only (per D-10014).
function findVillainGroupCards(registry, setAbbr, villainGroupSlug) {
    const setData = registry.getSet(setAbbr);
    if (!setData || !Array.isArray(setData.villains))
        return [];
    for (const group of setData.villains) {
        if (group.slug === villainGroupSlug && Array.isArray(group.cards)) {
            return group.cards;
        }
    }
    return [];
}
/**
 * Finds a villain group's display name (WP-677 — for S.H.I.E.L.D. Level membership).
 *
 * Separate from findVillainGroupCards (which has many callers and returns only the
 * cards) so this can read the group `name` without changing that shared signature.
 *
 * @param registry - Setup-time registry reader.
 * @param setAbbr - The set abbreviation.
 * @param villainGroupSlug - The group slug.
 * @returns The group's display name, or undefined if not found.
 */
function findVillainGroupName(registry, setAbbr, villainGroupSlug) {
    const setData = registry.getSet(setAbbr);
    if (!setData || !Array.isArray(setData.villains))
        return undefined;
    for (const group of setData.villains) {
        if (group.slug === villainGroupSlug) {
            return group.name;
        }
    }
    return undefined;
}
/**
 * Finds henchman group vAttack within the named set's henchmen[].
 *
 * No cross-set fallback exists — returns null if the named set is not
 * loaded or the group slug is not present in it.
 */
// why: WP-113 PS-7 mid-execution amendment — Builder Filtering Order —
//   iterate named set only (per D-10014).
function findHenchmanGroupVAttack(registry, setAbbr, henchmanGroupSlug) {
    const setData = registry.getSet(setAbbr);
    if (!setData || typeof setData !== 'object')
        return null;
    const castSetData = setData;
    if (!Array.isArray(castSetData.henchmen))
        return null;
    for (const entry of castSetData.henchmen) {
        if (!entry || typeof entry !== 'object')
            continue;
        const henchmanEntry = entry;
        if (henchmanEntry.slug === henchmanGroupSlug) {
            return {
                groupSlug: henchmanEntry.slug,
                vAttack: henchmanEntry.vAttack ?? null,
                // why: WP-677 — the group name for S.H.I.E.L.D. Level membership (e.g. "HYDRA …").
                groupName: henchmanEntry.name,
            };
        }
    }
    return null;
}
//# sourceMappingURL=economy.logic.js.map